import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Plus, Edit2, Trash2, Users, ArrowRight, RotateCcw, Eye } from 'lucide-react';
import { tenantsAPI, radiusServersAPI } from '../api';
import { Card, Button, Badge, Table, Pagination, Modal, Input, Select, PageHeader, Alert, useSort } from '../components/UI';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../TenantContext';

const MenuItem = ({ children, onClick, color, loading }) => (
  <button onClick={onClick} disabled={loading} style={{
    display: 'block', width: '100%', textAlign: 'left',
    padding: '9px 14px', background: 'none', border: 'none',
    cursor: loading ? 'wait' : 'pointer', fontSize: 13,
    color: color || 'var(--text-secondary)',
    transition: 'background 0.1s',
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}
  >{children}</button>
);

const DropdownPortal = ({ anchorRef, open, children }) => {
  const [pos, setPos] = React.useState({ top: 0, left: 0 });
  React.useEffect(() => {
    if (open && anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.right + window.scrollX });
    }
  }, [open, anchorRef]);
  if (!open) return null;
  return ReactDOM.createPortal(
    <div style={{ position: 'absolute', top: pos.top, left: pos.left, transform: 'translateX(-100%)', zIndex: 9999, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', minWidth: 160, overflow: 'hidden' }}
      onClick={e => e.stopPropagation()}>
      {children}
    </div>,
    document.body
  );
};

const EMPTY_FORM = { name: '', realm: '', description: '', radius_server_id: '', status: 'active' };

const TenantMenuButton = ({ id, t, openMenuId, setOpenMenuId, onlyDeleted, handleRestore, openEdit, setDeleteConfirm, setDeletePermanent, readOnly, isSuperadmin }) => {
  const btnRef = React.useRef(null);
  if (readOnly) {
    return (
      <Link to={`/users?tenant_id=${id}`} style={{ textDecoration: 'none' }}>
        <button style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Eye size={12} /> Lihat
        </button>
      </Link>
    );
  }
  return (
    <div>
      <button ref={btnRef}
        onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === t.id ? null : t.id); }}
        style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }}>
        ···
      </button>
      <DropdownPortal anchorRef={btnRef} open={openMenuId === t.id}>
        {onlyDeleted ? (
          <MenuItem color="var(--accent-green)" onClick={() => { handleRestore(t); setOpenMenuId(null); }}>↩ Restore</MenuItem>
        ) : (<>
          <Link to={`/users?tenant_id=${id}`} style={{ textDecoration: 'none' }}>
            <MenuItem onClick={() => setOpenMenuId(null)}>👥 Lihat Users</MenuItem>
          </Link>
          <MenuItem onClick={() => { openEdit(t); setOpenMenuId(null); }}>✏️ Edit</MenuItem>
        </>)}
        {(!onlyDeleted || isSuperadmin) && (<>
          <div style={{ borderTop: '1px solid var(--border)' }} />
          <MenuItem color="var(--accent-red)" onClick={() => { setDeleteConfirm(t); setDeletePermanent(false); setOpenMenuId(null); }}>🗑 Hapus</MenuItem>
        </>)}
      </DropdownPortal>
    </div>
  );
};

export default function Tenants() {
  const { user } = useAuth();
  const readOnly = user?.role === 'operator' || user?.role === 'user';
  const isSuperadmin = user?.role === 'superadmin';
  const navigate = useNavigate();
  const { selectTenant } = useTenant();
  const goToUsers = (tenant) => { selectTenant(tenant.id); navigate('/users'); };
  const [tenants, setTenants] = useState([]);
  const [servers, setServers] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [onlyDeleted, setOnlyDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deletePermanent, setDeletePermanent] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeletePermanent, setBulkDeletePermanent] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [perPage, setPerPage] = useState(15);

  useEffect(() => {
    if (!openMenuId) return;
    const handler = () => setOpenMenuId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openMenuId]);

  const { sort, onSortChange, sortParam } = useSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, per_page: perPage };
      if (q) params.q = q;
      if (onlyDeleted) params.only_deleted = true;
      if (sortParam) params.sort = sortParam;
      const res = await tenantsAPI.list(params);
      const allTenants = res.tenants || [];
      const filtered = statusFilter ? allTenants.filter(t => t.status === statusFilter) : allTenants;
      setTenants(filtered);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, q, statusFilter, onlyDeleted, perPage, sortParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); setSelected([]); }, [onlyDeleted]);

  useEffect(() => {
    // Role operator/user tidak punya akses ke /radius-servers (403) — skip fetch.
    if (readOnly) return;
    radiusServersAPI.list({ per_page: 100, status: 'active' })
      .then(r => setServers(r.radius_servers || [])).catch(() => {});
  }, [readOnly]);

  const openCreate = () => { if (readOnly) return; setEditTarget(null); setForm(EMPTY_FORM); setFormError(null); setModalOpen(true); };
  const openEdit = (t) => { if (readOnly) return; setEditTarget(t); setForm({ ...t, radius_server_id: t.radius_server_id || '' }); setFormError(null); setModalOpen(true); };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true); setFormError(null);
    try {
      const payload = { ...form };
      if (!payload.radius_server_id) delete payload.radius_server_id;
      if (editTarget) await tenantsAPI.update(editTarget.id, payload);
      else await tenantsAPI.create(payload);
      setModalOpen(false);
      setSuccess(editTarget ? 'Tenant diperbarui.' : 'Tenant dibuat.');
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (readOnly) return;
    const permanent = isSuperadmin && (onlyDeleted || deletePermanent);
    try {
      if (permanent) await tenantsAPI.deletePermanent(deleteConfirm.id);
      else await tenantsAPI.delete(deleteConfirm.id);
      setDeleteConfirm(null); setDeletePermanent(false);
      setSuccess(permanent ? 'Tenant dihapus permanen.' : `Tenant "${deleteConfirm.name}" dihapus.`);
      load();
    } catch (e) { setError(e.message); setDeleteConfirm(null); }
  };

  const handleRestore = async (t) => {
    if (readOnly) return;
    try { await tenantsAPI.restore(t.id); setSuccess('Tenant dipulihkan.'); load(); }
    catch (e) { setError(e.message); }
  };

  const handleBulkDelete = async () => {
    if (readOnly) return;
    const permanent = isSuperadmin && (onlyDeleted || bulkDeletePermanent);
    try {
      if (permanent) await Promise.all(selected.map(id => tenantsAPI.deletePermanent(id)));
      else await Promise.all(selected.map(id => tenantsAPI.delete(id)));
      setSuccess(`${selected.length} tenant ${permanent ? 'dihapus permanen' : 'dihapus'}.`);
      setSelected([]); setBulkDeleteConfirm(false); setBulkDeletePermanent(false); load();
    } catch (e) { setError(e.message); setBulkDeleteConfirm(false); }
  };

  const handleBulkRestore = async () => {
    if (readOnly) return;
    try {
      await Promise.all(selected.map(id => tenantsAPI.restore(id)));
      setSuccess(`${selected.length} tenant dipulihkan.`); setSelected([]); load();
    } catch (e) { setError(e.message); }
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const serverName = (id) => servers.find(s => s.id === id)?.name || `#${id}`;

  const columns = [
    { key: 'name', label: 'Name', sortable: true, render: (v, row) => (
      <button onClick={() => goToUsers(row)} title={`Lihat RADIUS user di tenant ${v}`}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 500, color: 'var(--accent-blue)', textDecoration: 'underline', textUnderlineOffset: 2, fontSize: 'inherit', fontFamily: 'inherit' }}>
        {v}
      </button>
    ) },
    { key: 'realm', label: 'Realm', sortable: true, render: v => <span className="mono" style={{ color: 'var(--text-mono)', fontSize: 12 }}>{v}</span> },
    { key: 'radius_server_id', label: 'Radius Server', render: v => v ? <span style={{ fontSize: 12, padding: '2px 8px', background: 'rgba(59,130,246,0.1)', color: 'var(--accent-blue)', borderRadius: 20, border: '1px solid rgba(59,130,246,0.2)' }}>{serverName(v)}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'created_at', label: 'Created', sortable: true, render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? new Date(v).toLocaleDateString('id-ID') : '—'}</span> },
    {
      key: 'id', label: '', render: (id, t) => (
        <TenantMenuButton id={id} t={t} openMenuId={openMenuId} setOpenMenuId={setOpenMenuId} onlyDeleted={onlyDeleted} handleRestore={handleRestore} openEdit={openEdit} setDeleteConfirm={setDeleteConfirm} setDeletePermanent={setDeletePermanent} readOnly={readOnly} isSuperadmin={isSuperadmin} />
      )
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="Tenants"
        subtitle={`${total} tenant${onlyDeleted ? ' (terhapus)' : ' terdaftar'}`}
        actions={
          readOnly ? null : (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selected.length > 0 && (<>
                {onlyDeleted && <Button variant="success" onClick={handleBulkRestore}><RotateCcw size={13} /> Restore {selected.length}</Button>}
                {(!onlyDeleted || isSuperadmin) && (
                  <Button variant="danger" onClick={() => setBulkDeleteConfirm(true)}>
                    <Trash2 size={13} /> Hapus {selected.length}
                  </Button>
                )}
              </>)}
              <Button variant={onlyDeleted ? 'secondary' : 'ghost'} onClick={() => setOnlyDeleted(d => !d)}>
                {onlyDeleted ? 'Tampil Aktif' : '🗑 Terhapus'}
              </Button>
              <Button onClick={openCreate}><Plus size={14} /> Tambah Tenant</Button>
            </div>
          )
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input placeholder="Cari nama atau realm…" value={q} onChange={e => { setQ(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 14px', color: 'var(--text-primary)', fontSize: 13, outline: 'none', fontFamily: 'var(--font-sans)', width: 260 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '8px 12px', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
          <option value="">Semua Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={tenants} loading={loading} sort={sort} onSortChange={onSortChange}
          emptyMessage={onlyDeleted ? 'Tidak ada data terhapus' : 'Belum ada tenant'}
          selected={readOnly ? undefined : selected} onSelect={readOnly ? undefined : setSelected} />
        <div style={{ padding: '0 16px' }}>
          <Pagination page={page} totalPages={totalPages} total={total} onChange={setPage} perPage={perPage} onPerPageChange={n => { setPerPage(n); setPage(1); }} />
        </div>
      </Card>

      {/* Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editTarget ? 'Edit Tenant' : 'Tambah Tenant'} width={480}>
        {formError && <Alert type="error" message={formError} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input label="Name *" value={form.name} onChange={e => f('name', e.target.value)} placeholder="ISP Jakarta" />
          <Input label="Realm *" value={form.realm} onChange={e => f('realm', e.target.value)} placeholder="isp-jakarta" />
          <Input label="Description" value={form.description} onChange={e => f('description', e.target.value)} />
          <Select label="Radius Server" value={form.radius_server_id} onChange={e => f('radius_server_id', e.target.value ? +e.target.value : '')}>
            <option value="">— Pilih server (opsional) —</option>
            {servers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.host})</option>)}
          </Select>
          <Select label="Status" value={form.status} onChange={e => f('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          {editTarget && form.radius_server_id !== editTarget.radius_server_id && (
            <div style={{ padding: '10px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--accent-orange)' }}>
              ⚠️ Mengganti radius server akan memicu migrasi otomatis — semua user & NAS akan dipindahkan.
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="ghost" onClick={() => setModalOpen(false)}>Batal</Button>
          <Button onClick={handleSave} loading={saving}>{editTarget ? 'Simpan' : 'Buat Tenant'}</Button>
        </div>
      </Modal>

      {/* Delete */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Hapus Tenant" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus tenant <strong style={{ color: 'var(--accent-red)' }}>{deleteConfirm?.name}</strong>?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Tenant ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={deletePermanent} onChange={e => setDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleDelete}>Hapus</Button>
        </div>
      </Modal>

      <Modal open={bulkDeleteConfirm} onClose={() => setBulkDeleteConfirm(false)} title="Hapus Bulk Tenant" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin menghapus <strong style={{ color: 'var(--accent-red)' }}>{selected.length} tenant</strong> sekaligus?
        </p>
        {isSuperadmin && (onlyDeleted ? (
          <p style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 20 }}>
            ⚠️ Tenant ini sudah berada di daftar terhapus, sehingga akan dihapus permanen dan tidak bisa dipulihkan lagi.
          </p>
        ) : (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={bulkDeletePermanent} onChange={e => setBulkDeletePermanent(e.target.checked)} style={{ accentColor: 'var(--accent-red)' }} />
            <span>Hapus permanen <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(tidak bisa dipulihkan)</span></span>
          </label>
        ))}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setBulkDeleteConfirm(false)}>Batal</Button>
          <Button variant="danger" onClick={handleBulkDelete}>Hapus Semua</Button>
        </div>
      </Modal>
    </div>
  );
}